// Copyright (c) StrangeBee 2023

import { Button, Spin } from 'antd';
import React, { ReactElement, useEffect } from 'react';
import { useLocalMessage } from '@Helpers/';
import { SearchScopeType } from '@Types';
import FiltersList from '@Organisms/Filter/FiltersList/FiltersList';
import { useListAllFields } from '@Queries/describeQueries';
import { ManageFiltersOutput } from '@Hooks/useFilters';
import { ICONS } from '@Constants/iconsApp';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THButton } from '@Molecules/Inputs/THButton/THButton';

import './SearchFilter.scss';

interface SearchFilterProps {
    fieldScope?: SearchScopeType;
    events: {
        onSearch: (keyword: string) => void;
        onClear: () => void;
        onAdd: () => void;
    };
    filterManager: ManageFiltersOutput;
    keyword?: string;
    onKeywordChange: (keyword: string) => void;
    loading: boolean;
}

const SearchFilter = ({
    fieldScope,
    events: { onSearch, onClear, onAdd },
    filterManager,
    keyword,
    onKeywordChange,
    loading,
}: SearchFilterProps): ReactElement => {
    const i = useLocalMessage();

    const { filters, tempFilters, setTempFilters, setEditFilters, removeTempFilter } = filterManager;

    const { data: describe } = useListAllFields();

    useEffect(() => {
        setEditFilters(true);
    }, [setEditFilters]);

    return (
        <div className="search-filter">
            <div className="search-filter__container">
                <div className="search-filter__container-title">
                    {i('search.filter.title')}
                    {fieldScope && ` / ${i(`search.scope.${fieldScope}.label`)}`}
                </div>

                <div className="search-filter__container-subtitle">{i('search.filter.subtitle')}</div>

                <THInput
                    value={keyword}
                    onChange={(event) => onKeywordChange(event.target.value)}
                    className="search-filter__keyword"
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                            onSearch(keyword || '');
                        }
                    }}
                />

                <div className="count-actions">
                    <div>
                        <span className="count-actions__number">{filters?.length}</span>{' '}
                        {i('search.filter.filter-number')}
                    </div>
                    <div>
                        <Button className="clear" type="link" disabled={!fieldScope} onClick={onAdd}>
                            {i('search.filter.add')}
                        </Button>
                        <Button className="clear" danger type="link" disabled={!fieldScope} onClick={onClear}>
                            {i('search.filter.clear')}
                        </Button>
                    </div>
                </div>

                <div className="search-filter__list">
                    {fieldScope && (
                        <FiltersList
                            removeTempFilter={removeTempFilter}
                            filters={tempFilters || []}
                            setFilters={setTempFilters}
                            vertical
                            describeField={describe?.[fieldScope]}
                        />
                    )}
                    {loading && (
                        <div className="search-filter__list__spin">
                            <Spin />
                        </div>
                    )}
                </div>

                <THButton
                    type="primary"
                    block
                    icon={<ICONS.SearchIcon />}
                    disabled={loading || !fieldScope || (!tempFilters?.length && !keyword)}
                    className="confirm"
                    onClick={() => onSearch(keyword || '')}
                >
                    {i('search.filter.search')}
                </THButton>
            </div>
        </div>
    );
};

export default SearchFilter;
